import { MasterCity } from "./cities";
import { Image } from "./images";
import { MasterPrefecture } from "./prefecture";

export interface Facility {
  id: string;
  companyId: string;
  name: string;
  zipCode: string | null;
  prefecture: number;
  city: number;
  address: string;
  tel: string;
  fax: string;
  url: string;
  latitude: number;
  longitude: number;
  category: SupportCategory;
  popularFeatures: PopularFeatureType[];
  staffTypes: StaffType[];
  medicalCare: MedicalCareType[];
  supportContents: SupportContentType[];
  acceptableAges: AcceptableAgeType[];
  parentSeparations: ParentSeparationType[];
  mealHelps: MealAssistanceType[];
  mealServices: MealServiceType[];
  isPublished?: boolean | null;
  commitmentsComment: string | null;
  availabilityMonday: AvailabilityType | null;
  availabilityTuesday: AvailabilityType | null;
  availabilityWednesday: AvailabilityType | null;
  availabilityThursday: AvailabilityType | null;
  availabilityFriday: AvailabilityType | null;
  availabilitySaturday: AvailabilityType | null;
  availabilitySunday: AvailabilityType | null;
  closingDays: string | null;
  businessHoursWeekday: string;
  businessHoursSaturday: string;
  businessHoursSunday: string;
  businessHoursHoliday: string;
  description: string;
  spotAvailability: AvailabilityType | null;
  disabilityPhysicalMobilityImpairment: DisabilityLevel[];
  disabilityPhysicalHearingImpairment: DisabilityLevel[];
  disabilityPhysicalVisualImpairment: DisabilityLevel[];
  disabilityDevelopmentalDisability: DisabilityLevel[];
  disabilityIntellectualDisability: DisabilityLevel[];
  disabilitySevereMultipleDisabilities: boolean;
  schedulesWithEntries?: ScheduleWithEntries[];
  masterCity?: MasterCity;
  masterPrefecture?: MasterPrefecture;
  currentContract?: CurrentContract;
  /** 現在有効な契約を除く過去の契約一覧（開始日の降順・運営限定） */
  pastContracts?: CurrentContract[];
  mainImages: Image[];
  categorySlug: string;
  categoryLabel: string;
  reviewsCount: number;
  reviewsAvgScore: string | null;
  adminMemo?: string;
  serviceAreas?: ServiceArea[];
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

export interface ServiceArea {
  city: number; // 市区町村コード
  facilityId: string;
  createdAt: string;
  updatedAt: string;
}

export interface ScheduleWithEntries extends Schedule {
  entries: ScheduleEntry[];
}

export interface CurrentContract {
  id: string;
  facilityId: string;
  type: ContractType;
  start: string;
  end: string;
  /** 入金管理メモ（運営限定。運営以外には API から返らない） */
  memo?: string;
  createdAt: string;
  updatedAt: string;
}

export interface FacilityGarellyImage {
  id: string;
  comment: string;
  dataUrl: string;
  imageUrl: string;
  thumbUrl: string;
  order: number;
  originalHeight: number;
  originalWidth: number;
  size: number;
  thumbnailHeight: number;
  thumbnailWidth: number;
  hashTags: HashTag[];
  facilityId: string;
  createdAt: string;
  updatedAt: string;
}

export interface FacilityNewsItem {
  id: string;
  facilityId: string;
  title: string;
  body: string;
  imageUrl: string | null;
  imageName: string | null;
  originalWidth: number | null;
  originalHeight: number | null;
  thumbUrl: string | null;
  thumbnailWidth: number | null;
  thumbnailHeight: number | null;
  dataUrl: string | null;
  isPublished: boolean;
  publishedAt: string | null;
  deletedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface HashTag {
  id: string;
  label: string;
  createdAt: string;
  updatedAt: string;
}

export interface Schedule {
  id: string;
  facilityId: string;
  title: string;
  description: string;
  order: number;
  deletedAt: string | null;
  createdAt: string;
  updatedAt: string;
  entries: ScheduleEntry[];
}

export interface ScheduleEntry {
  id: string;
  facilityScheduleId: string;
  time: string;
  label: string;
  description: string;
  order: number;
  deletedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface Movie {
  id: string;
  facilityId: string;
  dataUrl: string;
  createdAt: string;
  updatedAt: string;
  order: number;
}

export interface CreateFacilityGarellyImage {
  image: Image;
  comment: string;
  hashTags: HashTag[];
}

export interface FacilityCommitment {
  id: string;
  facilityId: string;
  title: string;
  description: string;
  imageUrl: string | null;
  imageName: string | null;
  originalWidth: number | null;
  originalHeight: number | null;
  thumbUrl: string | null;
  thumbnailWidth: number | null;
  thumbnailHeight: number | null;
  dataUrl: string | null;
  order: number;
  createdAt: string;
  updatedAt: string;
}

export interface Review {
  id: string;
  facilityId: string;
  score: number; // いいね評価　1~5
  status: ReviewStatus;
  decidingFactor: string; // この施設に決めた理由
  facilityComment: string | null; // 施設の素敵なところ・改善してほしいところ
  otherComment: string | null; // ご意見ご感想
  kidsInfo: string | null; // お子様の疾患・障がいについて
  kidsAge: string | null; // 利用し始めた年齢
  facility?: Facility;
  deletedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export enum SupportCategory {
  CHILD_DEVELOPMENT_CENTER = 1, // 児童発達支援センター・事業所
  AFTER_SCHOOL_DAY_SERVICE = 2, // 放課後等デイサービス
  HOME_NURSING = 3, // 訪問看護
  HOME_CARE = 4, // 訪問介護
  SHORT_STAY = 5, // 短期入所施設
  CONSULTATION_SUPPORT = 6, // 相談支援事業所
  NURSERY = 7, // 保育園
  HOME_MEDICAL_VISIT = 8, // 訪問診療
  HOME_PHARMACY_SERVICE = 9, // 訪問調剤
  NURSERY_VISIT_SUPPORT = 10, // 保育所等訪問支援
  HOME_DEVELOPMENT_SUPPORT = 11, // 居宅児童発達支援
  CHILD_CARE_SERVICE = 12, // 子ども預かり
  OTHER_SUPPORT = 0, // その他支援
}

export enum SupportContentType {
  OVERNIGHT_SHORT_STAY = 1, // 宿泊型短期入所
  TRANSPORT_SUPPORT = 2, // 移動支援
  HOME_VISIT_CARE = 3, // 居宅訪問介護
  DAYTIME_TEMPORARY_CARE = 4, // 日中一時
  HOME_RESPITE = 5, // 在宅レスパイト
  INDIVIDUAL_SUPPORT = 6, // 個別支援
  GROUP_SUPPORT = 7, // 集団支援
  EMERGENCY_24H_SUPPORT = 8, // 24時間緊急対応あり
  DAY_SHORT_STAY = 9, // 日帰りショートステイ
}

export enum StaffType {
  NURSERY_TEACHER = 1, // 保育士
  KINDERGARTEN_TEACHER = 2, // 幼稚園教諭
  SOCIAL_WORKER = 3, // 社会福祉士
  DOCTOR = 4, // 医師
  NURSE = 5, // 看護師
  PUBLIC_HEALTH_NURSE = 6, // 保健師
  CHILD_GUIDANCE_STAFF = 7, // 児童指導員
  PHYSICAL_THERAPIST = 8, // 理学療法士（PT）
  SPEECH_THERAPIST = 9, // 言語聴覚士（ST）
  OCCUPATIONAL_THERAPIST = 10, // 作業療法士（OT）
  MEDICAL_CARE_CHILD_COORDINATOR = 11, // 医療的ケア児コーディネーター
  CARE_WORKER = 12, // 介護福祉士
  CLINICAL_PSYCHOLOGIST = 13, // 臨床心理士
  CONSULTATION_SUPPORT_SPECIALIST = 14, // 相談支援専門員
}

/** 職種カテゴリ (7区分)。Occupation のグルーピング表示に用いる。サーバー OccupationCategory のミラー */
export enum OccupationCategory {
  MGMT = 1, // 管理・運営職
  RESP = 2, // 配置基準上の責任者職
  CARE = 3, // 直接支援・介護職
  CHILD = 4, // 児童・保育職
  MED = 5, // 医療・看護職
  REHAB = 6, // リハビリ・心理・栄養職
  // 7・8 は将来カテゴリ用に予約。OTHER は常に末尾として 9（90番台）に固定。
  OTHER = 9, // その他
}

/**
 * 職員の職種マスター (31種)。サーバー App\Enums\Occupation のミラー。
 * 値はカテゴリ別10番台区切り (十の位=カテゴリ番号)。サーバーと完全一致させること。
 */
export enum Occupation {
  // MGMT: 管理・運営職 (10番台)
  EXECUTIVE = 11, // 代表・経営者
  FACILITY_HEAD = 12, // 施設長
  MANAGER = 13, // 管理者
  CLERICAL_STAFF = 14, // 事務職員
  // RESP: 配置基準上の責任者職 (20番台)
  SERVICE_MANAGER = 21, // サービス管理責任者(サビ管)
  CHILD_DEVELOPMENT_MANAGER = 22, // 児童発達支援管理責任者(児発管)
  CONSULTATION_SUPPORT_SPECIALIST = 23, // 相談支援専門員
  CARE_MANAGER = 24, // 介護支援専門員(ケアマネ)
  SERVICE_PROVIDER_RESPONSIBLE = 25, // サービス提供責任者(サ責)
  CARE_PLAN_CREATOR = 26, // 計画作成担当者
  SOCIAL_WORKER = 27, // 社会福祉士・精神保健福祉士 (相談援助専門職)
  // CARE: 直接支援・介護職 (30番台)
  CERTIFIED_CARE_WORKER = 31, // 介護福祉士
  CARE_WORKER = 32, // 介護職員(無資格・初任者・実務者)
  LIFE_SUPPORT_WORKER = 33, // 生活支援員
  CARETAKER = 34, // 世話人
  VOCATIONAL_INSTRUCTOR = 35, // 職業指導員
  EMPLOYMENT_SUPPORT_WORKER = 36, // 就労支援員
  NIGHT_SUPPORT_WORKER = 37, // 夜間支援従事者
  // CHILD: 児童・保育職 (40番台)
  NURSERY_TEACHER = 41, // 保育士
  CHILD_GUIDANCE_STAFF = 42, // 児童指導員
  NURSERY_ASSISTANT = 43, // 保育補助・保育支援員
  // MED: 医療・看護職 (50番台)
  DOCTOR = 51, // 医師
  NURSE = 52, // 看護職員(看護師・准看護師・保健師)
  PHARMACIST = 53, // 薬剤師
  // REHAB: リハビリ・心理・栄養職 (60番台)
  PHYSICAL_THERAPIST = 61, // 理学療法士(PT)
  OCCUPATIONAL_THERAPIST = 62, // 作業療法士(OT)
  SPEECH_THERAPIST = 63, // 言語聴覚士(ST)
  PSYCHOLOGY_STAFF = 64, // 心理担当職員(公認心理師・臨床心理士)
  DIETITIAN = 65, // 管理栄養士・栄養士
  COOK = 66, // 調理員
  // OTHER: その他 (90番台)。70・80番台は将来カテゴリ用に予約し、その他は末尾の99に固定。
  OTHER = 99, // その他
}

export enum ReviewStatus {
  PENDING = 1, // 確認待ち
  PUBLISHED = 2, // 掲載中
  UNPUBLISHED = 3, // 非公開
}

export enum PopularFeatureType {
  WEEKEND_SUPPORT = 1, // 土日祝日対応あり
  TRANSPORTATION = 2, // 送迎あり
  BATH_SUPPORT = 3, // 入浴支援あり
  MEAL_PROVISION = 4, // 給食あり
  FULL_TIME_NURSE = 5, // 看護師常勤
  REHABILITATION_PT = 6, // リハビリ_PT 実施可能
  REHABILITATION_ST = 7, // リハビリ_ST 実施可能
  REHABILITATION_OT = 8, // リハビリ_OT 実施可能
}

export enum ParentSeparationType {
  SEPARATION_ALLOWED = 1, // 親子分離可
  PARENT_CHILD_USE = 2, // 親子利用
  CONDITIONAL_SEPARATION = 3, // 条件付き親子分離可
}

export enum MedicalCareType {
  ENTERAL_FEEDING = 1, // 経管栄養（胃管・腸管・胃ろう）
  MECHANICAL_VENTILATION = 2, // 人工呼吸器
  OXYGEN_THERAPY = 3, // 酸素投与
  SUCTION = 4, // 吸引
  NEBULIZER = 5, // ネブライザー（吸入）
  SUBCUTANEOUS_INJECTION = 6, // 皮下注射
  CENTRAL_VENOUS_CATHETER = 7, // 中心静脈カテーテルの管理
  STOOL_MANAGEMENT = 8, // 排便管理（ストーマ）
  CATHETERIZATION = 9, // 導尿
  TRACHEOSTOMY_MANAGEMENT = 10, // 気管切開の管理
}

export enum MealServiceType {
  REGULAR_MEAL = 1, // 普通食提供可能
  MINCED_MEAL = 2, // きざみ食提供可能
  PASTE_MEAL = 3, // ペースト食提供可能
  BRING_YOUR_MEAL = 4, // 食事持参
  NO_MEAL_PROVIDED = 5, // 食事提供なし
}

export enum MealAssistanceType {
  AVAILABLE = 1, // 食事介助可能
}

export enum DisabilityType {
  MOBILITY_IMPAIRMENT = 1, // 肢体不自由 (身体障害に属する)
  HEARING_IMPAIRMENT = 2, // 聴覚障害 (身体障害に属する)
  VISUAL_IMPAIRMENT = 3, // 視覚障害 (身体障害に属する)
  DEVELOPMENTAL_DISABILITY = 4, // 発達障害
  INTELLECTUAL_DISABILITY = 5, // 知的障害
  SEVERE_MULTIPLE_DISABILITIES = 6, // 重症心身障害
}

export enum DisabilityLevel {
  MILD = 1, // 軽度
  MODERATE = 2, // 中度
  SEVERE = 3, // 重度
  MOST_SEVERE = 4, // 最重度
}

export enum ContractType {
  LIGHT = 1, // ライト
  STANDARD = 2, // スタンダード
  PREMIUM = 3, // プレミアム
}

export enum AvailabilityType {
  AVAILABLE = 1, // 空きあり
  LIMITED_AVAILABILITY = 2, // やや空きあり
  FULL = 3, // 満員
}

export enum AcceptableAgeType {
  INFANT = 1, // 乳児（0〜2歳）
  TODDLER = 2, // 幼児（3〜6歳）
  ELEMENTARY_LOWER = 3, // 小学校低学年（6歳〜9歳）
  ELEMENTARY_UPPER = 4, // 小学校高学年（9歳〜12歳）
  MIDDLE_SCHOOL = 5, // 中学生
  HIGH_SCHOOL = 6, // 高校生
}
